import React, { useState, useEffect, useCallback, useMemo } from "react";
import Offcanvas from "react-bootstrap/Offcanvas";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Autocomplete,
  Checkbox,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import {
  PiUserLight,
  PiMailboxLight,
  PiChairLight,
  PiAddressBookLight,
  PiIdentificationCardLight,
  PiMapPinSimpleAreaLight,
  PiNewspaperClippingLight,
  PiGenderMaleLight,
  PiPencilSimpleLineLight,
} from "react-icons/pi";
import { CiMobile3 } from "react-icons/ci";
import { RxCross2 } from "react-icons/rx";
import styles from "./Candidate.module.css";
import { useDispatch, useSelector } from "react-redux";
import { resumeProcces } from "../../redux/internal-tracking/InternalTrackerSlice";
import { addSkill, allSkill } from "../../redux/common/skillSlice";
import {
  addWorkAuthorization,
  allWorkAuthorization,
} from "../../redux/common/workAuthorizationSlice";
import { addLocation, allLocation } from "../../redux/common/locationSlice";
import { customTextColor, whichFile } from "../../utils/const";
import { debounce } from "lodash";
import { createSelector } from "reselect";
import isEqual from "lodash/isEqual";
import {
  createCandidate,
  createHotList,
} from "../../redux/hotlist/hotListSlice";

// Redux selectors
const selectTracker = (state) => state.tracker;
const selectSkill = (state) => state.skill;
const selectWorkAutho = (state) => state.workautho;
const selectLocation = (state) => state.location;

export const selectResumeData = createSelector(
  [selectTracker],
  (tracker) => tracker.resumeData
);
export const selectSkillData = createSelector(
  [selectSkill],
  (skill) => skill.data?.locations || []
);
export const selectWorkAuthoData = createSelector(
  [selectWorkAutho],
  (workautho) => workautho.data?.work_authorization_data || []
);
export const selectLocationsData = createSelector(
  [selectLocation],
  (location) => location.data?.locations || []
);

// Field styling
const fieldStyle = {
  width: "100%",
  "& .MuiInputBase-root": {
    paddingLeft: "30px",
  },
  "& .MuiInputLabel-root": {
    left: "35px",
    fontSize: "14px",
  },
  "& .MuiInputBase-input": {
    paddingBottom: "7px",
  },
  "& .MuiInputLabel-shrink": {
    left: "35px",
    fontSize: "14px",
  },
  padding: "7px",
  marginLeft: "5px",
};

// Gender options
const genderOptions = [
  { id: "Male", name: "Male" },
  { id: "Female", name: "Female" },
];

// Willing to relocate options
const relocateOptions = [
  { id: "yes", value: true, name: "Yes" },
  { id: "no", value: false, name: "No" },
];

const CreateHotlistDrawer = ({
  show,
  handleClose,
  hotListID,
  hotListTitle,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const stableDispatch = useCallback(dispatch, []);
  const location = useLocation();
  const { prevData, returnSelectedFileName } = location.state || {};

  const [selectedFileName, setSelectedFileName] = useState("");
  const [base64Resume, setBase64Resume] = useState("");
  const [resumeError, setResumeError] = useState(false);
  const [hasProcessedResume, setHasProcessedResume] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(
    !hotListID || !hotListTitle
  );
  const [title, setTitle] = useState(hotListTitle || "My Hotlist for Active");
  const [hotListId, setHotListId] = useState(hotListID);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isResumeChanged, setIsResumeChanged] = useState(false);

  const resumeData = useSelector(selectResumeData);
  const skillData = useSelector(selectSkillData);
  const workauthoData = useSelector(selectWorkAuthoData);
  const locationsData = useSelector(selectLocationsData);

  const initialFormValues = {
    full_name: "",
    current_job_title: "",
    total_experience: "",
    mobile_no: "",
    alternative_mobile_no: "",
    email: "",
    work_authorization: "",
    current_location: "",
    prefer_location: [],
    primary_skills: [],
    secondary_skills: [],
    resume: "",
    gender: "",
    willing_to_relocate: "",
    rate: "",
  };

  const formik = useFormik({
    initialValues: prevData
      ? {
          full_name: prevData.full_name || "",
          current_job_title: prevData.current_job_title || "",
          total_experience: prevData.total_experience || "",
          mobile_no: prevData.mobile_no || "",
          alternative_mobile_no: prevData.alternative_mobile_no || "",
          email: prevData.email || "",
          work_authorization: prevData.work_authorization || "",
          current_location: prevData.current_location || "",
          prefer_location: prevData.prefer_location || [],
          primary_skills: prevData.primary_skills || [],
          secondary_skills: prevData.secondary_skills || [],
          resume: prevData.resume || "",
          gender: prevData.gender || "",
          willing_to_relocate: prevData.willing_to_relocate ? "yes" : "no",
          rate: prevData.rate || "",
        }
      : initialFormValues,
    validationSchema: Yup.object({
      full_name: Yup.string().required("Full Name is required"),
      current_job_title: Yup.string().required("Job Title is required"),
      total_experience: Yup.string().required("Experience is required"),
      mobile_no: Yup.string()
        .matches(
          /^[\d\s.\-()+]*$/,
          "Mobile Number must be a valid phone number"
        )
        .required("Mobile Number is required"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      work_authorization: Yup.string().required(
        "Work Authorization is required"
      ),
      current_location: Yup.string().required("Current Location is required"),
      prefer_location: Yup.array()
        .of(Yup.string())
        .min(1, "Prefer Location is required")
        .required("Prefer Location is required"),
      resume: Yup.string().required("Resume is required"),
      gender: Yup.string().required("Gender is required"),
      willing_to_relocate: Yup.string()
        .oneOf(["yes", "no"], "Please select Yes or No")
        .required("Willing to Relocate is required"),
      rate: Yup.number()
        .required("Rate is required")
        .integer("Rate must be an integer")
        .min(1, "Rate must be at least 1"),
      primary_skills: Yup.array()
        .of(Yup.string())
        .min(1, "At least one Primary Skill is required")
        .required("Primary Skill is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);
      try {
        let currentHotListId = hotListId;

        if (!currentHotListId) {
          const hotListResponse = await stableDispatch(
            createHotList({ name: title })
          ).unwrap();
          currentHotListId = hotListResponse?.id;
          setHotListId(currentHotListId);
          setIsEditingTitle(false);
        }

        const submitValues = {
          ...values,
          willing_to_relocate: values.willing_to_relocate === "yes",
          hot_list: currentHotListId,
        };

        const candidateResponse = await stableDispatch(
          createCandidate(submitValues)
        ).unwrap();

        if (candidateResponse) {
          setFormSubmitted(true);
          resetForm({ values: initialFormValues });
          setSelectedFileName("");
          setBase64Resume("");
          setHasProcessedResume(false);
          setResumeError(false);
          setIsResumeChanged(false);
        }
      } catch (error) {
        console.error("Error submitting form:", error);
      } finally {
        setLoading(false);
      }
    },
  });

  // Reset state when drawer is closed
  useEffect(() => {
    if (!show) {
      setHotListId(hotListID);
      setTitle(hotListTitle || "My Hotlist for Active");
      setIsEditingTitle(!hotListID || !hotListTitle);
      setFormSubmitted(false);
      setSelectedFileName("");
      setBase64Resume("");
      setHasProcessedResume(false);
      setResumeError(false);
      setIsResumeChanged(false);
      formik.resetForm({ values: initialFormValues });
    }
  }, [show, hotListID, hotListTitle, formik]);

  // Initial data fetch
  useEffect(() => {
    if (!locationsData.length) stableDispatch(allLocation());
    if (!workauthoData.length) stableDispatch(allWorkAuthorization());
    if (!skillData.length) stableDispatch(allSkill());
  }, [
    stableDispatch,
    locationsData.length,
    workauthoData.length,
    skillData.length,
  ]);

  // Debounced resume processing
  const debouncedResumeProcess = useCallback(
    debounce((formData) => {
      setLoading(true);
      stableDispatch(resumeProcces(formData))
        .unwrap()
        .then(() => {
          setHasProcessedResume(true);
        })
        .catch((error) => {
          console.error("Resume processing error:", error);
          setResumeError(true);
        })
        .finally(() => {
          setLoading(false);
        });
    }, 500),
    [stableDispatch]
  );

  // Handle file upload
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(",")[1];
        const fileExtension = file.name.split(".").pop().toLowerCase();
        const formattedBase64 = `data:${whichFile(
          fileExtension
        )};base64,${base64String}`;
        setBase64Resume(formattedBase64);
        formik.setFieldValue("resume", formattedBase64);
        setResumeError(false);
        setIsResumeChanged(true);
        const formData = new FormData();
        formData.append("resume_file", file);
        debouncedResumeProcess(formData);
      };
      reader.readAsDataURL(file);
      setSelectedFileName(file.name);
    } else {
      setResumeError(true);
    }
  };

  // Consolidated update logic for resume data
  const updateFormik = useMemo(
    () =>
      debounce(async (currentValues) => {
        if (
          !resumeData ||
          !hasProcessedResume ||
          !locationsData.length ||
          !workauthoData.length ||
          !isResumeChanged
        ) {
          return;
        }

        const newValues = {};

        if (resumeData?.data?.processed_json) {
          const {
            name,
            job_profile,
            total_experience,
            phone,
            alternate_contact,
            email,
            gender,
            willing_to_relocate,
            rate,
          } = resumeData.data.processed_json;

          newValues.full_name = name || prevData?.full_name || "";
          newValues.current_job_title =
            job_profile || prevData?.current_job_title || "";
          newValues.total_experience =
            total_experience || prevData?.total_experience || "";
          newValues.mobile_no = phone || prevData?.mobile_no || "";
          newValues.alternative_mobile_no =
            alternate_contact || prevData?.alternative_mobile_no || "";
          newValues.email = email || prevData?.email || "";

          if (gender) {
            const genderOption = genderOptions.find(
              (opt) => opt.name.toLowerCase() === gender.toLowerCase()
            );
            newValues.gender = genderOption?.id || prevData?.gender || "";
          } else {
            newValues.gender = prevData?.gender || "";
          }

          newValues.willing_to_relocate =
            willing_to_relocate !== undefined
              ? willing_to_relocate
                ? "yes"
                : "no"
              : prevData?.willing_to_relocate
              ? "yes"
              : "no";

          newValues.rate =
            rate && !isNaN(parseInt(rate))
              ? parseInt(rate)
              : prevData?.rate || "";
        }

        const currentLocation =
          resumeData?.data?.processed_json?.current_location;
        if (currentLocation) {
          const existingLocation = locationsData.find(
            (loc) => loc.name.toLowerCase() === currentLocation.toLowerCase()
          );
          if (existingLocation) {
            newValues.current_location = existingLocation.id;
          } else {
            try {
              const newLocation = await stableDispatch(
                addLocation({ name: currentLocation })
              ).unwrap();
              if (newLocation?.id) {
                newValues.current_location = newLocation.id;
                await stableDispatch(allLocation());
              }
            } catch (error) {
              console.error("Error adding location:", error);
            }
          }
        } else {
          newValues.current_location = prevData?.current_location || "";
        }

        const preferLocations =
          resumeData?.data?.processed_json?.prefer_location || [];
        if (preferLocations.length) {
          const locationIds = [];
          for (const loc of preferLocations) {
            const existingLocation = locationsData.find(
              (l) => l.name.toLowerCase() === l.toLowerCase()
            );
            if (existingLocation) {
              locationIds.push(existingLocation.id);
            } else {
              try {
                const newLocation = await stableDispatch(
                  addLocation({ name: loc })
                ).unwrap();
                if (newLocation?.id) {
                  locationIds.push(newLocation.id);
                  await stableDispatch(allLocation());
                }
              } catch (error) {
                console.error("Error adding preferred location:", error);
              }
            }
          }
          newValues.prefer_location = locationIds.length
            ? locationIds
            : prevData?.prefer_location || [];
        } else {
          newValues.prefer_location = prevData?.prefer_location || [];
        }

        const workAuthorization =
          resumeData?.data?.processed_json?.work_authorization;
        if (workAuthorization) {
          const existingWorkAuth = workauthoData.find(
            (auth) =>
              auth.name.toLowerCase() === workAuthorization.toLowerCase()
          );
          if (existingWorkAuth) {
            newValues.work_authorization = existingWorkAuth.id;
          } else {
            try {
              const newWorkAuth = await stableDispatch(
                addWorkAuthorization({ name: workAuthorization })
              ).unwrap();
              if (newWorkAuth?.id) {
                newValues.work_authorization = newWorkAuth.id;
                await stableDispatch(allWorkAuthorization());
              }
            } catch (error) {
              console.error("Error adding work authorization:", error);
            }
          }
        } else {
          newValues.work_authorization = prevData?.work_authorization || "";
        }

        if (resumeData?.data?.processed_json?.skills && skillData.length) {
          const allSkillsMap = skillData.reduce((acc, skill) => {
            acc[skill.name.toLowerCase()] = skill.id;
            return acc;
          }, {});

          const mapSkillsToIds = (skillsList) => {
            const skillIds = [];
            const missingSkills = [];
            skillsList?.forEach((skill) => {
              const skillLower = skill.toLowerCase();
              if (allSkillsMap[skillLower]) {
                skillIds.push(allSkillsMap[skillLower]);
              } else {
                missingSkills.push(skill);
              }
            });
            return { skillIds, missingSkills };
          };

          const primarySkills =
            resumeData.data.processed_json.skills.primary_skills || [];
          const secondarySkills =
            resumeData.data.processed_json.skills.secondary_skills || [];

          const primary = mapSkillsToIds(primarySkills);
          const secondary = mapSkillsToIds(secondarySkills);

          newValues.primary_skills = primary.skillIds.length
            ? primary.skillIds
            : prevData?.primary_skills || [];
          newValues.secondary_skills = secondary.skillIds.length
            ? secondary.skillIds
            : prevData?.secondary_skills || [];

          const missingSkills = [
            ...primary.missingSkills,
            ...secondary.missingSkills,
          ];
          if (missingSkills.length) {
            try {
              await Promise.all(
                missingSkills.map((skill) =>
                  stableDispatch(addSkill({ name: skill }))
                )
              );
              await stableDispatch(allSkill()).unwrap();
              const updatedSkillsMap = skillData.reduce((acc, skill) => {
                acc[skill.name.toLowerCase()] = skill.id;
                return acc;
              }, {});
              const updatedPrimary = mapSkillsToIds(
                resumeData.data.processed_json.skills.primary_skills || []
              );
              const updatedSecondary = mapSkillsToIds(
                resumeData.data.processed_json.skills.secondary_skills || []
              );
              newValues.primary_skills = updatedPrimary.skillIds.length
                ? updatedPrimary.skillIds
                : prevData?.primary_skills || [];
              newValues.secondary_skills = updatedSecondary.skillIds.length
                ? updatedSecondary.skillIds
                : prevData?.secondary_skills || [];
            } catch (error) {
              console.error("Error adding skills:", error);
            }
          }
        } else {
          newValues.primary_skills = prevData?.primary_skills || [];
          newValues.secondary_skills = prevData?.secondary_skills || [];
        }

        // Only update if newValues differ from currentValues
        if (
          Object.keys(newValues).length &&
          !isEqual(newValues, currentValues)
        ) {
          formik.setValues({ ...currentValues, ...newValues });
        }
      }, 500),
    [
      resumeData,
      hasProcessedResume,
      locationsData,
      workauthoData,
      skillData,
      prevData,
      stableDispatch,
      isResumeChanged,
    ]
  );

  // Trigger updates when resumeData or hasProcessedResume changes
  useEffect(() => {
    if (isResumeChanged) {
      updateFormik(formik.values);
    }
  }, [resumeData, hasProcessedResume, isResumeChanged, updateFormik]);

  // Handle returnSelectedFileName
  useEffect(() => {
    if (returnSelectedFileName) {
      setSelectedFileName(returnSelectedFileName);
    }
  }, [returnSelectedFileName]);

  // Handle resume field
  useEffect(() => {
    if (!prevData?.resume) {
      formik.setFieldValue("resume", base64Resume || "");
    }
    setResumeError(false);
  }, [base64Resume, prevData, formik.setFieldValue]);

  const handleButtonClick = () => {
    document.getElementById("fileInput").click();
  };

  const handleFullNameChange = (event) => {
    const { value } = event.target;
    if (/^[a-zA-Z\s]*$/.test(value)) {
      formik.setFieldValue("full_name", value);
    }
  };

  const handleMobileNoChange = (e) => {
    const value = e.target.value;
    if (/^[\d\s.\-()+]*$/.test(value) && value.length <= 15) {
      formik.setFieldValue("mobile_no", value);
    }
  };

  const handleAlternativeMobileNoChange = (e) => {
    const value = e.target.value;
    if (/^[\d\s.\-()+]*$/.test(value) && value.length <= 15) {
      formik.setFieldValue("alternative_mobile_no", value);
    }
  };

  const handleRateChange = (e) => {
    const value = e.target.value;
    const numericValue = value.replace(/[^0-9]/g, "");
    if (numericValue === "" || !isNaN(parseInt(numericValue))) {
      formik.setFieldValue("rate", numericValue ? parseInt(numericValue) : "");
    }
  };

  const formatRateDisplay = (value) => {
    return value ? `$${value} hourly` : "";
  };

  const handleTitleChange = (event) => {
    setTitle(event.target.value);
  };

  const handleEditTitle = () => {
    if (isEditingTitle) {
      setIsEditingTitle(false);
    } else {
      setIsEditingTitle(true);
    }
  };

  return (
    <Offcanvas
      show={show}
      onHide={handleClose}
      placement="end"
      scroll={true}
      backdrop={false}
      style={{
        border: "none",
        marginTop: "65px",
        width: "60%",
        boxShadow: "-4px 0 10px rgba(0, 0, 0, 0.15)",
        backgroundColor: "#fff",
      }}
    >
      <Offcanvas.Body style={{ padding: 0, margin: 0 }}>
        <Box sx={{ padding: "10px 10px" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0px 10px",
            }}
          >
            {isEditingTitle ? (
              <TextField
                value={title}
                onChange={handleTitleChange}
                onBlur={handleEditTitle}
                onKeyPress={(e) => {
                  if (e.key === "Enter") handleEditTitle();
                }}
                autoFocus
                variant="standard"
                sx={{ flexGrow: 1, marginRight: "10px" }}
                inputProps={{
                  style: { color: customTextColor, fontWeight: "bold" },
                }}
              />
            ) : (
              <Typography
                style={{
                  color: customTextColor,
                  fontWeight: "bold",
                  cursor: hotListId ? "default" : "pointer",
                }}
                onClick={hotListId ? null : handleEditTitle}
              >
                {title}{" "}
                {!hotListId && (
                  <PiPencilSimpleLineLight style={{ marginLeft: "5px" }} />
                )}
              </Typography>
            )}
            <RxCross2
              style={{
                fontSize: "15px",
                cursor: "pointer",
                color: customTextColor,
              }}
              onClick={handleClose}
            />
          </Box>
          <Box
            className={styles.upload_resume_section}
            sx={{
              padding: "5px 15px",
              margin: "5px 10px",
              borderRadius: "3px",
              bgcolor: "#f5f5f5",
              mt: 1,
            }}
          >
            <Box style={{ color: customTextColor }} sx={{ marginTop: "10px" }}>
              <Typography
                sx={{
                  marginBottom: "2px !important",
                  fontSize: "20px !important",
                }}
              >
                Unlimited Hotlist – Not Be Limit
              </Typography>
              <Typography>
                Boost highest possibility reaching to recruiters by filling
                below details
              </Typography>
            </Box>
            <Box textAlign={"right"} sx={{ marginTop: "10px" }}>
              <button
                onClick={handleButtonClick}
                disabled={loading}
                style={loading ? { padding: "7px 40px" } : {}}
              >
                {loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Upload Resume"
                )}
              </button>
              <input
                id="fileInput"
                type="file"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              {selectedFileName && (
                <p
                  style={{
                    marginTop: "6px",
                    fontSize: "14px",
                    color: "#1b89de",
                  }}
                >
                  Selected file: {selectedFileName}
                </p>
              )}
              {resumeError && (
                <div className={styles.error}>Upload resume is required</div>
              )}
            </Box>
          </Box>

          <form onSubmit={formik.handleSubmit}>
            <Grid container className={styles.candidate_field_section}>
              <Grid
                item
                xs={12}
                sm={12}
                md={6}
                lg={6}
                sx={{ padding: "5px 5px 0 5px", position: "relative" }}
              >
                <Box
                  className={styles.icon_style}
                  style={{
                    top:
                      (formik.touched.gender && formik.errors.gender) ||
                      (formik.touched.full_name && formik.errors.full_name)
                        ? "45%"
                        : "62%",
                  }}
                >
                  <PiUserLight style={{ fontSize: "25px" }} />
                </Box>
                <TextField
                  id="full_name"
                  label="Candidate Name"
                  variant="standard"
                  name="full_name"
                  onChange={handleFullNameChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.full_name}
                  sx={fieldStyle}
                  disabled={loading}
                />
                {formik.touched.full_name && formik.errors.full_name && (
                  <div className={styles.error}>{formik.errors.full_name}</div>
                )}
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={6}
                lg={6}
                sx={{ padding: "5px 10px 0 10px", position: "relative" }}
              >
                <Box
                  className={styles.icon_style}
                  style={{
                    top:
                      (formik.touched.gender && formik.errors.gender) ||
                      (formik.touched.full_name && formik.errors.full_name)
                        ? "45%"
                        : "62%",
                  }}
                >
                  <PiGenderMaleLight style={{ fontSize: "25px" }} />
                </Box>
                <TextField
                  id="gender"
                  select
                  label="Gender"
                  variant="standard"
                  name="gender"
                  value={formik.values.gender}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  sx={fieldStyle}
                  disabled={loading}
                >
                  {genderOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </TextField>
                {formik.touched.gender && formik.errors.gender && (
                  <div className={styles.error}>{formik.errors.gender}</div>
                )}
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={6}
                lg={6}
                sx={{ padding: "5px 10px 0 10px", position: "relative" }}
              >
                <Box
                  className={styles.icon_style}
                  style={{
                    top:
                      formik.touched.mobile_no && formik.errors.mobile_no
                        ? "45%"
                        : "62%",
                  }}
                >
                  <CiMobile3 style={{ fontSize: "25px" }} />
                </Box>
                <TextField
                  id="mobile_no"
                  label="Contact No"
                  variant="standard"
                  type="text"
                  name="mobile_no"
                  onChange={handleMobileNoChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.mobile_no}
                  sx={fieldStyle}
                  disabled={loading}
                />
                {formik.touched.mobile_no && formik.errors.mobile_no && (
                  <div className={styles.error}>{formik.errors.mobile_no}</div>
                )}
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={6}
                lg={6}
                sx={{ padding: "5px 10px 0 10px", position: "relative" }}
              >
                <Box
                  className={styles.icon_style}
                  style={{
                    top:
                      formik.touched.mobile_no && formik.errors.mobile_no
                        ? "45%"
                        : "62%",
                  }}
                >
                  <CiMobile3 style={{ fontSize: "25px" }} />
                </Box>
                <TextField
                  id="alternative_mobile_no"
                  label="Alternative Contact No (Optional)"
                  variant="standard"
                  type="text"
                  name="alternative_mobile_no"
                  onChange={handleAlternativeMobileNoChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.alternative_mobile_no}
                  sx={fieldStyle}
                  disabled={loading}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={6}
                lg={6}
                sx={{ padding: "5px 10px 0 10px", position: "relative" }}
              >
                <Box
                  className={styles.icon_style}
                  style={{
                    top:
                      (formik.touched.current_job_title &&
                        formik.errors.current_job_title) ||
                      (formik.touched.email && formik.errors.email)
                        ? "45%"
                        : "62%",
                  }}
                >
                  <PiMailboxLight style={{ fontSize: "25px" }} />
                </Box>
                <TextField
                  id="email"
                  label="E-Mail ID"
                  variant="standard"
                  type="text"
                  name="email"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                  sx={fieldStyle}
                  disabled={loading}
                />
                {formik.touched.email && formik.errors.email && (
                  <div className={styles.error}>{formik.errors.email}</div>
                )}
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={6}
                lg={6}
                sx={{ padding: "5px 10px 0 10px", position: "relative" }}
              >
                <Box
                  className={styles.icon_style}
                  style={{
                    top:
                      (formik.touched.current_job_title &&
                        formik.errors.current_job_title) ||
                      (formik.touched.email && formik.errors.email)
                        ? "45%"
                        : "62%",
                  }}
                >
                  <PiChairLight style={{ fontSize: "25px" }} />
                </Box>
                <TextField
                  id="current_job_title"
                  label="Current Job Profile"
                  variant="standard"
                  type="text"
                  name="current_job_title"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.current_job_title}
                  sx={fieldStyle}
                  disabled={loading}
                />
                {formik.touched.current_job_title &&
                  formik.errors.current_job_title && (
                    <div className={styles.error}>
                      {formik.errors.current_job_title}
                    </div>
                  )}
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={6}
                lg={6}
                sx={{ padding: "5px 10px 0 10px", position: "relative" }}
              >
                <Box
                  className={styles.icon_style}
                  style={{
                    top:
                      (formik.touched.work_authorization &&
                        formik.errors.work_authorization) ||
                      (formik.touched.total_experience &&
                        formik.errors.total_experience)
                        ? "45%"
                        : "62%",
                  }}
                >
                  <PiAddressBookLight style={{ fontSize: "25px" }} />
                </Box>
                <TextField
                  id="total_experience"
                  label="Total Experience"
                  variant="standard"
                  type="text"
                  name="total_experience"
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/[^0-9.]/g, "");
                    formik.setFieldValue("total_experience", rawValue);
                  }}
                  onBlur={formik.handleBlur}
                  value={
                    formik.values.total_experience
                      ? `${formik.values.total_experience} ${
                          formik.values.total_experience > 1 ? "Years" : "Year"
                        }`
                      : ""
                  }
                  sx={fieldStyle}
                  disabled={loading}
                />
                {formik.touched.total_experience &&
                  formik.errors.total_experience && (
                    <div className={styles.error}>
                      {formik.errors.total_experience}
                    </div>
                  )}
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={6}
                lg={6}
                sx={{ padding: "5px 10px 0 10px", position: "relative" }}
              >
                <Box
                  className={styles.icon_style}
                  style={{
                    top:
                      (formik.touched.work_authorization &&
                        formik.errors.work_authorization) ||
                      (formik.touched.total_experience &&
                        formik.errors.total_experience)
                        ? "45%"
                        : "62%",
                  }}
                >
                  <PiIdentificationCardLight style={{ fontSize: "25px" }} />
                </Box>
                <TextField
                  id="work_authorization"
                  select
                  label="Work Authorization"
                  variant="standard"
                  name="work_authorization"
                  value={formik.values.work_authorization}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  sx={fieldStyle}
                  disabled={loading}
                >
                  {workauthoData.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </TextField>
                {formik.touched.work_authorization &&
                  formik.errors.work_authorization && (
                    <div className={styles.error}>
                      {formik.errors.work_authorization}
                    </div>
                  )}
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={6}
                lg={6}
                sx={{ padding: "5px 10px 0 10px", position: "relative" }}
              >
                <Box
                  className={styles.icon_style}
                  style={{
                    top:
                      (formik.touched.willing_to_relocate &&
                        formik.errors.willing_to_relocate) ||
                      (formik.touched.current_location &&
                        formik.errors.current_location)
                        ? "45%"
                        : "62%",
                  }}
                >
                  <PiMapPinSimpleAreaLight style={{ fontSize: "25px" }} />
                </Box>
                <TextField
                  id="current_location"
                  select
                  label="Current Location"
                  variant="standard"
                  name="current_location"
                  value={formik.values.current_location}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  sx={fieldStyle}
                  disabled={loading}
                >
                  {locationsData.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </TextField>
                {formik.touched.current_location &&
                  formik.errors.current_location && (
                    <div className={styles.error}>
                      {formik.errors.current_location}
                    </div>
                  )}
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={6}
                lg={6}
                sx={{ padding: "5px 10px 0 10px", position: "relative" }}
              >
                <Box
                  className={styles.icon_style}
                  style={{
                    top:
                      (formik.touched.willing_to_relocate &&
                        formik.errors.willing_to_relocate) ||
                      (formik.touched.current_location &&
                        formik.errors.current_location)
                        ? "45%"
                        : "62%",
                  }}
                >
                  <PiMapPinSimpleAreaLight style={{ fontSize: "25px" }} />
                </Box>
                <TextField
                  id="willing_to_relocate"
                  select
                  label="Willing to Relocate"
                  variant="standard"
                  name="willing_to_relocate"
                  value={formik.values.willing_to_relocate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  sx={fieldStyle}
                  disabled={loading}
                >
                  {relocateOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </TextField>
                {formik.touched.willing_to_relocate &&
                  formik.errors.willing_to_relocate && (
                    <div className={styles.error}>
                      {formik.errors.willing_to_relocate}
                    </div>
                  )}
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={6}
                lg={6}
                sx={{ padding: "5px 10px 0 10px", position: "relative" }}
              >
                <Box
                  className={styles.icon_style}
                  style={{
                    top:
                      formik.touched.rate && formik.errors.rate ? "45%" : "62%",
                  }}
                >
                  <PiNewspaperClippingLight style={{ fontSize: "25px" }} />
                </Box>
                <TextField
                  id="rate"
                  label="Hourly Rate"
                  variant="standard"
                  name="rate"
                  onChange={handleRateChange}
                  onBlur={formik.handleBlur}
                  value={formatRateDisplay(formik.values.rate)}
                  sx={fieldStyle}
                  disabled={loading}
                />
                {formik.touched.rate && formik.errors.rate && (
                  <div className={styles.error}>{formik.errors.rate}</div>
                )}
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={6}
                lg={6}
                sx={{ padding: "5px 10px 10px 10px", position: "relative" }}
              >
                <Box
                  className={styles.icon_style}
                  style={{
                    top:
                      (formik.touched.prefer_location &&
                        formik.errors.prefer_location) ||
                      (formik.touched.rate && formik.errors.rate)
                        ? "45%"
                        : "62%",
                  }}
                >
                  <PiMapPinSimpleAreaLight style={{ fontSize: "25px" }} />
                </Box>
                <Autocomplete
                  multiple
                  id="prefer_location-autocomplete"
                  options={locationsData}
                  disableCloseOnSelect
                  value={locationsData.filter((location) =>
                    formik.values.prefer_location.includes(location.id)
                  )}
                  onChange={(event, value) => {
                    const selectedValues = value.map((location) => location.id);
                    formik.setFieldValue("prefer_location", selectedValues);
                  }}
                  onBlur={formik.handleBlur}
                  getOptionLabel={(option) => option.name}
                  renderOption={(props, option, { selected }) => (
                    <MenuItem {...props}>
                      <Checkbox
                        checked={selected}
                        sx={{ marginRight: 0, marginLeft: 0 }}
                      />
                      <span>{option.name}</span>
                    </MenuItem>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Preferred Location"
                      variant="standard"
                      name="prefer_location"
                      sx={fieldStyle}
                    />
                  )}
                  sx={{ backgroundColor: "#fff", width: "100%" }}
                  disabled={loading}
                />
                {formik.touched.prefer_location &&
                  formik.errors.prefer_location && (
                    <div className={styles.error}>
                      {formik.errors.prefer_location}
                    </div>
                  )}
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                lg={12}
                sx={{ padding: "5px 10px 10px 10px", position: "relative" }}
              >
                <Box
                  className={styles.icon_style}
                  style={{
                    top:
                      formik.touched.primary_skills &&
                      formik.errors.primary_skills
                        ? "40%"
                        : "60%",
                  }}
                >
                  <PiNewspaperClippingLight style={{ fontSize: "25px" }} />
                </Box>
                <Autocomplete
                  multiple
                  id="primary-skills-autocomplete"
                  options={skillData}
                  disableCloseOnSelect
                  value={skillData.filter((skill) =>
                    formik.values.primary_skills.includes(skill.id)
                  )}
                  onChange={(event, value) => {
                    const selectedValues = value.map((skill) => skill.id);
                    formik.setFieldValue("primary_skills", selectedValues);
                  }}
                  onBlur={formik.handleBlur}
                  getOptionLabel={(option) => option.name}
                  renderOption={(props, option, { selected }) => (
                    <MenuItem {...props}>
                      <Checkbox
                        checked={selected}
                        sx={{ marginRight: 0, marginLeft: 0 }}
                      />
                      <span>{option.name}</span>
                    </MenuItem>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Primary Skills"
                      variant="standard"
                      name="primary_skills"
                      sx={fieldStyle}
                    />
                  )}
                  sx={{ backgroundColor: "#fff", width: "100%" }}
                  disabled={loading}
                />
                {formik.touched.primary_skills &&
                  formik.errors.primary_skills && (
                    <div className={styles.error}>
                      {formik.errors.primary_skills}
                    </div>
                  )}
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                lg={12}
                sx={{ padding: "5px 10px 10px 10px", position: "relative" }}
              >
                <Box
                  className={styles.icon_style}
                  style={{
                    top:
                      formik.touched.secondary_skills &&
                      formik.errors.secondary_skills
                        ? "40%"
                        : "60%",
                  }}
                >
                  <PiNewspaperClippingLight style={{ fontSize: "25px" }} />
                </Box>
                <Autocomplete
                  multiple
                  id="secondary-skills-autocomplete"
                  options={skillData}
                  disableCloseOnSelect
                  value={skillData.filter((skill) =>
                    formik.values?.secondary_skills?.includes(skill.id)
                  )}
                  onChange={(event, value) => {
                    const selectedValues = value.map((skill) => skill.id);
                    formik.setFieldValue("secondary_skills", selectedValues);
                  }}
                  onBlur={formik.handleBlur}
                  getOptionLabel={(option) => option.name}
                  renderOption={(props, option, { selected }) => (
                    <MenuItem {...props}>
                      <Checkbox
                        checked={selected}
                        sx={{ marginRight: 0, marginLeft: 0 }}
                      />
                      <span>{option.name}</span>
                    </MenuItem>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Secondary Skills"
                      variant="standard"
                      name="secondary_skills"
                      sx={fieldStyle}
                    />
                  )}
                  sx={{ backgroundColor: "#fff", width: "100%" }}
                  disabled={loading}
                />
              </Grid>
            </Grid>
            <Box className={styles.add_consultant_btns}>
              <button type="submit" disabled={loading || formik.isSubmitting}>
                {loading
                  ? "Loading..."
                  : formSubmitted
                  ? "Add Another Candidate"
                  : "Save to Hotlist"}
              </button>
            </Box>
          </form>
        </Box>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default CreateHotlistDrawer;
